import React, { useState, useRef, useEffect } from 'react';

// Default fallback image path - a known-good image that exists in the deployment
const DEFAULT_FALLBACK_IMAGE = '/images/services/residential-1.webp';

/**
 * OptimizedImage - Lazy loading image component with placeholder
 * Improves page load speed by only loading images when they're in viewport
 * Includes explicit dimensions to prevent CLS (Cumulative Layout Shift)
 * Includes onError fallback to prevent blank image boxes
 */
export function OptimizedImage({ 
  src, 
  alt, 
  className = '', 
  loading = 'lazy',
  decoding = 'async',
  width,
  height,
  aspectRatio = '16/9',
  fallbackSrc = DEFAULT_FALLBACK_IMAGE,
  ...props 
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const imgRef = useRef(null);

  useEffect(() => {
    // Reset error state when src changes
    setHasError(false);
    setCurrentSrc(src);
  }, [src]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before image enters viewport
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleError = () => {
    if (!hasError && fallbackSrc && currentSrc !== fallbackSrc) {
      console.warn(`Image failed to load: ${currentSrc}, falling back to: ${fallbackSrc}`);
      setHasError(true);
      setCurrentSrc(fallbackSrc);
      setIsLoaded(false);
    }
  };

  // Compute style for aspect ratio to prevent CLS
  const containerStyle = width && height 
    ? { width, height } 
    : { aspectRatio };

  return (
    <div 
      ref={imgRef} 
      className={`relative overflow-hidden ${className}`} 
      style={containerStyle}
      {...props}
    >
      {/* Placeholder with blur effect */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 bg-stone-200"
          style={{ 
            background: 'linear-gradient(90deg, #e7e5e4 25%, #d6d3d1 50%, #e7e5e4 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite'
          }}
        />
      )}
      
      {/* Actual image - only loads src when in view */}
      <img
        src={isInView ? currentSrc : undefined}
        data-src={currentSrc}
        alt={alt}
        loading={loading}
        decoding={decoding}
        width={width}
        height={height}
        onLoad={() => setIsLoaded(true)}
        onError={handleError}
        className={`w-full h-full object-cover transition-opacity duration-500 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
}

/**
 * ImageWithFallback - Simple image component with error fallback
 * Use this for regular img tags that need fallback support
 * Does not include lazy loading optimization
 */
export function ImageWithFallback({
  src,
  alt,
  fallbackSrc = DEFAULT_FALLBACK_IMAGE,
  className = '',
  onError: externalOnError,
  ...props
}) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setCurrentSrc(src);
    setHasError(false);
  }, [src]);

  const handleError = (e) => {
    if (!hasError && fallbackSrc && currentSrc !== fallbackSrc) {
      console.warn(`Image failed to load: ${currentSrc}, falling back to: ${fallbackSrc}`);
      setHasError(true);
      setCurrentSrc(fallbackSrc);
    }
    if (externalOnError) {
      externalOnError(e);
    }
  };

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      onError={handleError}
      {...props}
    />
  );
}

/**
 * handleImageError - Utility function to add to any img tag's onError
 * Sets the src to a fallback image if the original fails to load
 * Usage: <img src="..." onError={(e) => handleImageError(e)} />
 */
export const handleImageError = (e, fallbackSrc = DEFAULT_FALLBACK_IMAGE) => {
  const img = e.target;
  if (img.src !== fallbackSrc && !img.dataset.fallbackAttempted) {
    console.warn(`Image failed to load: ${img.src}, falling back to: ${fallbackSrc}`);
    img.dataset.fallbackAttempted = 'true';
    img.src = fallbackSrc;
  }
};

export default OptimizedImage;
